import { Todo } from '@/types/todo';
import { loadTimeSettings } from './timeSettings';
import { createDeadline } from './todoHelpers';

// Secret trial tracking - hidden from user data resets
const SECRET_TRIAL_KEY = 'app_meta_v1';

interface SecretTrialData {
  completedTaskCount: number;
  firstTaskDate: string;
}

export interface PaywallState {
  payments: PaymentRecord[];
  hasPendingPayment: boolean;
}

export interface PaymentRecord {
  id: string;
  amount: number;
  date: string;
  stripeSessionId?: string;
  type: 'daily' | 'clearOverdue';
}

// Get current paywall state from localStorage
export function getPaywallState(): PaywallState {
  const stored = localStorage.getItem('paywallState');
  if (!stored) {
    return {
      payments: [],
      hasPendingPayment: false
    };
  }
  const parsed = JSON.parse(stored);
  // Handle legacy state format
  return {
    payments: parsed.payments || [],
    hasPendingPayment: parsed.hasPendingPayment || false
  };
}

// Save paywall state to localStorage
export function savePaywallState(state: PaywallState): void {
  localStorage.setItem('paywallState', JSON.stringify(state));
}

// Check if there are overdue tasks
export function hasOverdueTasks(todos: Todo[]): boolean {
  const now = new Date();
  return todos.some(todo => !todo.completed && now.getTime() > todo.deadline.getTime() && !todo.isOverdrive);
}

// Check if it's currently end of day time (inclusive - from end hour until reset)
export function isEndOfDayTime(): boolean {
  const now = new Date();
  const { endOfDayHour, resetHour } = loadTimeSettings();
  
  // If we're at or past the end of day hour, consider it "end of day time"
  // This continues until the daily reset hour
  if (endOfDayHour >= resetHour) {
    // Normal case: end hour is after reset hour (e.g., 23 -> 7)
    return now.getHours() >= endOfDayHour || now.getHours() < resetHour;
  } else {
    // Edge case: end hour is before reset hour (e.g., 7 -> 23)
    return now.getHours() >= endOfDayHour && now.getHours() < resetHour;
  }
}

// Check if current time is past the end of initial day for any overdue task
export function isPastEndOfInitialDay(todos: Todo[]): boolean {
  const now = new Date();
  const { endOfDayHour } = loadTimeSettings();
  
  return todos.some(todo => {
    if (todo.completed || todo.isOverdrive) return false;
    
    // Check if task is overdue
    if (now.getTime() <= todo.deadline.getTime()) return false;
    
    // Get the end-of-day time for the day this task was due
    const taskDueDate = new Date(todo.deadline);
    const endOfTaskDay = new Date(taskDueDate.getFullYear(), taskDueDate.getMonth(), taskDueDate.getDate(), endOfDayHour, 0, 0);
    
    // Return true if we're past the end-of-day hour for when this task was originally due
    return now.getTime() > endOfTaskDay.getTime();
  });
}

// Get today's date string
export function getTodayString(): string {
  return new Date().toDateString();
}

// Check if paywall should be triggered (simplified logic)
export function shouldTriggerPaywall(todos: Todo[]): boolean {
  // Paywall disabled - remove this line to re-enable
  return false;
  
  const activeTodos = todos.filter(todo => !todo.completed);
  const completedTodos = todos.filter(todo => todo.completed);
  
  // Debug logging
  const isPastEndOfInitial = isPastEndOfInitialDay(activeTodos);
  const hasOverdue = hasOverdueTasks(activeTodos);
  const hasPayment = hasAlreadyPaidThisCycle();
  const secretCompletedCount = getSecretTrialCount();
  
  // console.log('Paywall check:', {
  //   isPastEndOfInitial,
  //   hasOverdue,
  //   hasPayment,
  //   completedCount: completedTodos.length,
  //   secretCompletedCount,
  //   activeTodos: activeTodos.length,
  //   currentHour: new Date().getHours()
  // });
  
  // Free trial: Don't trigger until at least 5 tasks have been completed
  if (secretCompletedCount < 5) {
    // console.log('Paywall: Free trial active (completed tasks < 5)');
    return false;
  }
  
  // Don't trigger if user has already paid this cycle
  if (hasPayment) {
    // console.log('Paywall: Already paid this cycle');
    return false;
  }
  
  // New rule: Show paywall if it's past end of initial day AND there are overdue tasks
  const shouldTrigger = isPastEndOfInitial && hasOverdue;
  // console.log('Paywall decision:', shouldTrigger);
  
  return shouldTrigger;
}

// Check if user has already paid in the current paywall cycle
export function hasAlreadyPaidThisCycle(): boolean {
  const paywallState = getPaywallState();
  const { resetHour } = loadTimeSettings();
  const now = new Date();
  
  // Calculate the start of the current paywall cycle (last reset time)
  const currentCycleStart = new Date();
  currentCycleStart.setHours(resetHour, 0, 0, 0);
  
  // If current time is before reset hour, the cycle started yesterday
  if (now.getHours() < resetHour) {
    currentCycleStart.setDate(currentCycleStart.getDate() - 1);
  }
  
  // Check for payments made within the current cycle
  const cyclePayment = paywallState.payments.some(payment => {
    const paymentDate = new Date(payment.date);
    return paymentDate >= currentCycleStart && (payment.type === 'daily' || payment.type === 'clearOverdue');
  });
  
  return cyclePayment;
}

// Legacy function for backward compatibility
export function hasAlreadyPaidToday(): boolean {
  return hasAlreadyPaidThisCycle();
}


// Set pending payment state
export function setPendingPayment(): void {
  const paywallState = getPaywallState();
  
  const newState: PaywallState = {
    ...paywallState,
    hasPendingPayment: true
  };
  
  savePaywallState(newState);
}

// Secret trial functions - hidden from regular data resets
export function getSecretTrialCount(): number {
  try {
    const stored = localStorage.getItem(SECRET_TRIAL_KEY);
    if (!stored) return 0;
    const data: SecretTrialData = JSON.parse(stored);
    return data.completedTaskCount || 0;
  } catch {
    return 0;
  }
}

export function updateSecretTrialCount(): void {
  try {
    const stored = localStorage.getItem(SECRET_TRIAL_KEY);
    let data: SecretTrialData;
    
    if (!stored) {
      data = {
        completedTaskCount: 1,
        firstTaskDate: new Date().toISOString()
      };
    } else {
      const existing: SecretTrialData = JSON.parse(stored);
      data = {
        ...existing,
        completedTaskCount: (existing.completedTaskCount || 0) + 1
      };
    }
    
    localStorage.setItem(SECRET_TRIAL_KEY, JSON.stringify(data));
  } catch {
    // Silent fail - don't break the app if localStorage is unavailable
  }
}

// Dismiss paywall (only after confirmed payment)
export function dismissPaywall(): void {
  const paywallState = getPaywallState();
  
  const newState: PaywallState = {
    ...paywallState,
    hasPendingPayment: false
  };
  
  savePaywallState(newState);
}


// Add payment record
export function addPaymentRecord(amount: number, stripeSessionId?: string, type: 'daily' | 'clearOverdue' = 'daily'): void {
  const paywallState = getPaywallState();
  const now = new Date();
  
  const payment: PaymentRecord = {
    id: Date.now().toString(),
    amount,
    date: now.toISOString(),
    stripeSessionId,
    type
  };
  
  const newState: PaywallState = {
    ...paywallState,
    payments: [payment, ...paywallState.payments],
    hasPendingPayment: false
  };
  
  savePaywallState(newState);
  
  // Dispatch event to notify components
  window.dispatchEvent(new CustomEvent('paywallStateChanged'));
}

// Get time until end of day
export function getTimeUntilEndOfDay(): string {
  const now = new Date();
  const { endOfDayHour } = loadTimeSettings();
  
  const endOfDay = new Date();
  endOfDay.setHours(endOfDayHour, 0, 0, 0);
  
  // If we're past end of day, calculate for tomorrow
  if (now.getTime() > endOfDay.getTime()) {
    endOfDay.setDate(endOfDay.getDate() + 1);
  }
  
  const diff = endOfDay.getTime() - now.getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

// Clear all overdue tasks by converting them to overdrive
export function clearOverdueTasks(todos: Todo[]): Todo[] {
  const now = new Date();
  return todos.map(todo => {
    // If task is overdue and not completed, convert to overdrive
    if (!todo.completed && now > todo.deadline) {
      return {
        ...todo,
        isOverdrive: true,
        title: todo.title.startsWith('🚀 ') ? todo.title : `🚀 ${todo.title}`,
        deadline: createDeadline(), // New deadline for today
        createdAt: new Date() // Reset creation time for fair countdown
      };
    }
    return todo;
  });
}

// Clear paywall state when user resolves issues
export function clearPaywallState(): void {
  const paywallState = getPaywallState();
  
  const newState: PaywallState = {
    ...paywallState,
    hasPendingPayment: false
  };
  
  savePaywallState(newState);
  
  // Dispatch event to notify components
  window.dispatchEvent(new CustomEvent('paywallStateChanged'));
}

// Reset daily state (called during daily reset)
export function resetDailyPaywallState(): void {
  const paywallState = getPaywallState();
  
  const newState: PaywallState = {
    ...paywallState,
    hasPendingPayment: false
  };
  
  savePaywallState(newState);
}